<template>
    <div>
        <!-- Original state fallback when no changelogs exist -->
        <div v-if="!changeLogs.length && marking" class="card mb-3">
            <div class="card-body">
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <div class="comment-meta d-flex align-items-center small font-weight-bold">
                        <span class="mr-2">{{ formatTime(marking.created_at) }}</span>
                        <span>{{ formatDate(marking.created_at) }}</span>
                    </div>
                </div>

                <div class="d-inline-flex align-items-center">
                    <span class="badge badge-light d-inline-flex align-items-center mr-2">
                        <b-icon icon="file-earmark" class="mr-1"></b-icon>
                        Original
                    </span>
                    <span class="font-italic">Initial values (no changes recorded)</span>
                </div>

                <div v-if="originalItems.length" class="changed-keys mt-2">
                    <div class="mb-2">Current values:</div>
                    <div
                        v-for="(item, i) in originalItems"
                        :key="i"
                        class="text-pre-wrap">
                        <div v-if="item.key === 'catalog_1'">
                            <strong>Requires approval:</strong>
                            <span class="approval-selection col-sm-8">
                                <template v-for="color in marking.tool.catalog_1_values">
                                    <div class="approval"
                                         :class="color.value === item.value ? 'active' : ''"
                                         :style="'background-color: ' + color.value"></div>
                                </template>
                            </span>
                        </div>
                        <div v-else>
                            <strong>{{ item.title }}:</strong>
                            <span>{{ item.value }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Regular changelog entries -->
        <div
            v-for="(log, index) in changeLogs"
            :key="index"
            class="card mb-3">
            <div class="card-body">
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <div class="comment-meta d-flex align-items-center small font-weight-bold">
                        <span class="mr-2">{{ formatTime(log.created_at) }}</span>
                        <span>{{ formatDate(log.created_at) }}</span>
                    </div>

                    <div class="comment-created_by">
                        {{ log.user_name }}
                    </div>
                </div>

                <div class="d-inline-flex align-items-center">
                    <span
                        class="badge badge-light d-inline-flex align-items-center mr-2"
                        style="z-index: 1;">
                        <b-icon
                            :icon="log.action.platform === 'browser' ? 'display' : 'phone'"
                            class="mr-1">
                        </b-icon>
                        {{ log.action.platform === 'browser' ? 'SaaS' : 'App' }}
                    </span>
                    <span class="mr-1">Action:</span>
                    <span class="font-italic">
                        {{ log.action.lifecycle }}
                    </span>
                </div>

                <div v-if="getChangeItems(log).length" class="changed-keys mt-2">
                    <div class="mb-2">{{ log.action.lifecycle === 'created' ? 'Initial values:' : 'Changes:' }}</div>
                    <div
                        v-for="(item, i) in getChangeItems(log)"
                        :key="i"
                        class="text-pre-wrap">
                        <!-- Colors-->
                        <div v-if="item.title === 'catalog_1'">
                            <strong>Requires approval:</strong>
                            <template v-if="log.action.lifecycle !== 'created'">
                                <span class="approval-selection"
                                      :class="showActivePending ? 'col-sm-4' : 'col-sm-8'">
                                    <template v-for="color in marking.tool.catalog_1_values">
                                        <div class="approval"
                                             :class="color.value === item.from ? 'active' : ''"
                                             :style="'background-color: ' + color.value"></div>
                                    </template>
                                </span>
                                <span class="change_arrow">→</span>
                            </template>
                            <span class="approval-selection"
                                  :class="showActivePending ? 'col-sm-4' : 'col-sm-8'">
                                <template v-for="color in marking.tool.catalog_1_values">
                                    <div class="approval"
                                         :class="color.value === item.to ? 'active' : ''"
                                         :style="'background-color: ' + color.value"></div>
                                </template>
                            </span>
                        </div>

                        <div v-else-if="item.title === 'is_checked'">
                            <strong>Checked:</strong>
                            <template v-if="log.action.lifecycle !== 'created'">
                                <span>{{ item.from === true || item.from === 1 ? 'OK' : (item.from === '—' ? '—' : 'Not OK') }}</span>
                                <span class="change_arrow">→</span>
                            </template>
                            <span>{{ item.to === true || item.to === 1 ? 'OK' : (item.to === '—' ? '—' : 'Not OK') }}</span>
                        </div>

                        <div v-else>
                            <strong>{{ item.title }}:</strong>
                            <template v-if="log.action.lifecycle !== 'created'">
                                <span>{{ item.from }}</span>
                                <span class="change_arrow">→</span>
                            </template>
                            <span>{{ item.to }}</span>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";

export default {
    name: "ChangeLogHistory",
    props: {
        changeLogs: {
            type: Array,
            required: true,
            default: () => []
        },
        marking: {
            type: Object,
            required: false,
            default: null
        },
        showActivePending: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        originalItems() {
            if (!this.marking) return [];
            const m = this.marking;
            const items = [];

            if (m.markingIndex !== null && m.markingIndex !== undefined) {
                items.push({ key: 'index', title: 'index', value: m.markingIndex });
            }
            if (m.description) {
                items.push({ key: 'description', title: 'description', value: m.description });
            }
            if (m.title) {
                items.push({ key: 'title', title: 'title', value: m.title });
            }
            if (m.is_checked !== null && m.is_checked !== undefined) {
                items.push({ key: 'is_checked', title: 'Checked', value: m.is_checked ? 'OK' : 'Not OK' });
            }
            if (m.catalog_1 !== null && m.catalog_1 !== undefined && m.catalog_1 !== '') {
                items.push({ key: 'catalog_1', title: 'Requires approval', value: m.catalog_1 });
            }
            for (let i = 2; i <= 5; i++) {
                const val = m['catalog_' + i];
                if (val !== null && val !== undefined && val !== '') {
                    items.push({ key: 'catalog_' + i, title: this.getCatalogTitle(i, null), value: this.catalogDisplayText(i, val) });
                }
            }
            for (let i = 0; i <= 5; i++) {
                const val = m['text_' + i];
                if (val !== null && val !== undefined && val !== '') {
                    items.push({ key: 'text_' + i, title: 'text_' + i, value: val });
                }
            }

            return items;
        }
    },
    methods: {
        formatTime(date) {
            return moment(date).format("HH:mm");
        },
        formatDate(date) {
            return moment(date).format("DD.MM.YYYY");
        },

        hiddenChangeKeys() {
            return new Set([
                "x",
                "y",
                "is_locked",
                "x1",
                "y1",
                "x2",
                "y2",
                "points",
                "page",
                "tool",
                "type",
                "uuid",
                "project_marking_tool_id",
                "deleted_at",
                "created_by",
                "updated_by",
                "screen_type",
                "groupped_attachments",
                "prefix"
            ]);
        },

        areSameValue(a, b) {
            const normalize = (v) => {
                if (v === null || v === undefined || v === "") return null;
                if (typeof v === 'boolean') return v ? 1 : 0;
                return v;
            };

            const av = normalize(a);
            const bv = normalize(b);

            if (av === bv) return true;

            if ((typeof av === "number" || typeof av === "string") &&
                (typeof bv === "number" || typeof bv === "string")) {
                return av.toString() === bv.toString();
            }

            if (typeof av === "object" || typeof bv === "object") {
                try {
                    return JSON.stringify(av) === JSON.stringify(bv);
                } catch (e) {
                    return false;
                }
            }

            return false;
        },

        getCatalogIndexFromKey(key) {
            const match = /^catalog_(\d+)$/.exec(key);
            return match ? parseInt(match[1], 10) : null;
        },

        getCatalogTitle(catalogIndex, fallbackTool) {
            const key = "catalog_" + catalogIndex;
            const tool = (this.marking && this.marking.tool) ? this.marking.tool : fallbackTool;
            const label = tool && tool[key] ? tool[key] : null;
            return label || ("Catalog_" + catalogIndex);
        },

        catalogDisplayText(catalogIndex, rawValue) {
            if (rawValue === null || rawValue === undefined || rawValue === "") {
                return "—";
            }

            const tool = this.marking && this.marking.tool ? this.marking.tool : null;
            const valuesKey = "catalog_" + catalogIndex + "_values";
            const values = tool ? tool[valuesKey] : null;

            if (Array.isArray(values)) {
                const match = values.find(v => {
                    if (!v) return false;
                    if (v.value === null || v.value === undefined) return false;
                    return v.value.toString() === rawValue.toString();
                });

                if (match && match.text) {
                    return match.text;
                }
            }

            return rawValue;
        },

        normalizeDisplayValue(value) {
            if (value === null || value === undefined || value === "") return "—";
            return value;
        },

        getChangeItems(log) {
            const changed = log && log.changed_keys ? log.changed_keys : {};
            const oldKeys = log && log.old_keys ? log.old_keys : {};
            const fallbackTool = oldKeys && oldKeys.tool ? oldKeys.tool : null;

            const hidden = this.hiddenChangeKeys();

            return Object.keys(changed)
                .filter((key) => !hidden.has(key))
                .map((key) => {
                    const fromRaw = (oldKeys && oldKeys[key] !== undefined) ? oldKeys[key] : null;
                    const toRaw = (changed[key] !== undefined) ? changed[key] : null;

                    if (this.areSameValue(fromRaw, toRaw)) {
                        return null;
                    }

                    const catalogIndex = this.getCatalogIndexFromKey(key);

                    if (catalogIndex !== null && catalogIndex !== 1) {
                        return {
                            key,
                            title: this.getCatalogTitle(catalogIndex, fallbackTool),
                            from: this.catalogDisplayText(catalogIndex, fromRaw),
                            to: this.catalogDisplayText(catalogIndex, toRaw),
                        };
                    }

                    let title = key;
                    if (key === "is_locked") title = "Locked";

                    return {
                        key,
                        title,
                        from: this.normalizeDisplayValue(fromRaw),
                        to: this.normalizeDisplayValue(toRaw),
                    };
                })
                .filter(Boolean);
        }
    }
};
</script>

<style scoped>
.changed-keys {
    background: #f0f0f0;
    border-radius: 3px;
    padding: 8px;
}

.text-pre-wrap {
    white-space: pre-wrap;
}

.change_arrow {
    color: #dc2626;
    font-size: 24px;
    margin: 0 4px;
}
</style>
